import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { apiTenant } from "../lib/api"

interface Payslip {
  id: string
  basicEarned: string
  grossPay: string
  epfEmployee: string
  apitAmount: string
  netPay: string
  contentHash: string
  run: { periodStart: string; periodEnd: string; status: string; ruleSetId: string }
}

interface PayslipDetail {
  payslip: Payslip & {
    fullNameSnap: string
    employeeNoSnap: string
    nicSnap: string | null
    epfNoSnap: string | null
    bodyJson: Record<string, unknown>
    lineItems: Array<{
      id: string
      category: "earning" | "deduction" | "statutory" | "informational"
      code: string
      description: string
      quantity: string | null
      rate: string | null
      amount: string
    }>
    taxLineItems: Array<{
      id: string
      tableId: string
      bracketFrom: string
      bracketTo: string | null
      rate: string
      taxableAmount: string
      taxAmount: string
    }>
  }
  verification: { stored: string; recomputed: string; verified: boolean }
}

function fmtMoney(s: string | number): string {
  return Number(s).toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function MyPayslips() {
  const { slug = "" } = useParams<{ slug: string }>()
  const [openId, setOpenId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ["my-payslips", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<{ payslips: Payslip[] }>(slug, "/me/payslips"),
  })

  function handleDownload(id: string, periodStart: string) {
    const token = localStorage.getItem("people.token") ?? ""
    const url =
      (import.meta.env.PROD ? "/people/api" : "/api") + `/v1/t/${slug}/me/payslips/${id}/pdf`
    fetch(url, { headers: { authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.blob()
      })
      .then((blob) => {
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        link.download = `payslip-${periodStart.slice(0, 10)}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
      })
      .catch((e) => alert(`Download failed: ${e.message}`))
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">My payslips</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded">
        {list.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {list.error && <div className="p-6 text-sm text-red-600">{(list.error as Error).message}</div>}
        {list.data && list.data.payslips.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-400">No payslips yet.</div>
        )}
        {list.data && list.data.payslips.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Period</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Gross</th>
                <th className="text-right px-4 py-2 font-medium">EPF 8%</th>
                <th className="text-right px-4 py-2 font-medium">APIT</th>
                <th className="text-right px-4 py-2 font-medium">Net</th>
                <th className="text-right px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {list.data.payslips.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs">
                    {p.run.periodStart.slice(0, 10)} → {p.run.periodEnd.slice(0, 10)}
                  </td>
                  <td className="px-4 py-2 text-xs">{p.run.status}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtMoney(p.grossPay)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtMoney(p.epfEmployee)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtMoney(p.apitAmount)}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(p.netPay)}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => setOpenId(p.id)}
                      className="text-xs text-brand-600 hover:text-brand-800 mr-3"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDownload(p.id, p.run.periodStart)}
                      className="text-xs text-emerald-700 hover:text-emerald-900"
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId && <PayslipModal slug={slug} payslipId={openId} onClose={() => setOpenId(null)} />}
    </div>
  )
}

function PayslipModal(props: { slug: string; payslipId: string; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ["my-payslip", props.slug, props.payslipId],
    queryFn: () => apiTenant<PayslipDetail>(props.slug, `/me/payslips/${props.payslipId}`),
  })

  const ps = detail.data?.payslip
  const v = detail.data?.verification

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-semibold">Payslip detail</div>
          <button onClick={props.onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>

        {detail.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {detail.error && <div className="p-6 text-sm text-red-600">{(detail.error as Error).message}</div>}

        {ps && v && (
          <div className="p-5 space-y-4">
            <div
              className={`text-xs rounded p-2 ${
                v.verified
                  ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              {v.verified ? "✓ Hash verified" : "✗ Hash mismatch — contact HR"}
            </div>

            <Block title="Earnings" items={ps.lineItems.filter((l) => l.category === "earning")} />
            <Block title="Deductions" items={ps.lineItems.filter((l) => l.category === "deduction")} />
            <Block title="Statutory" items={ps.lineItems.filter((l) => l.category === "statutory")} />

            <div className="bg-slate-50 border border-slate-200 rounded p-3 grid grid-cols-2 gap-2 text-sm">
              <div>Gross</div>
              <div className="text-right font-mono">{fmtMoney(ps.grossPay)}</div>
              <div className="font-semibold">Net pay</div>
              <div className="text-right font-mono font-semibold text-base">{fmtMoney(ps.netPay)}</div>
            </div>

            <Block
              title="Employer contributions (informational)"
              items={ps.lineItems.filter((l) => l.category === "informational")}
            />

            <div className="text-[10px] text-slate-400 font-mono break-all">SHA-256: {ps.contentHash}</div>
          </div>
        )}
      </div>
    </div>
  )
}

function Block(props: {
  title: string
  items: Array<{ id: string; description: string; amount: string }>
}) {
  if (props.items.length === 0) return null
  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-2">{props.title}</div>
      <table className="w-full text-xs border border-slate-200 rounded">
        <tbody>
          {props.items.map((i) => (
            <tr key={i.id} className="border-b border-slate-100 last:border-b-0">
              <td className="px-2 py-1">{i.description}</td>
              <td className="px-2 py-1 text-right font-mono">{fmtMoney(i.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
